import { useEffect, useMemo, useState } from "react";
import './App.css';
import '@iq/models/styles.css'
import './index.css';
import useUserStore from "./states/user";
import useCustomerStore from "./states/customers";
import useProductCatalogStore from "./states/productCatalog";
import { filterAppCatalog, NavBar, PageHeader } from '@iq/models'

import { AiOutlineShop, AiOutlineForm, AiOutlineTag } from "react-icons/ai";
import { IoReceiptOutline } from "react-icons/io5";
import { FiUsers } from "react-icons/fi";
import useIsSmallScreen from "./hooks/useIsSmallScreen.js"
import Catalog from "./pages/Catalog"
import VariableProductPage from "./pages/variableProductPage/[id].jsx"
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import Orders from "./pages/Orders"
import Customers from "./pages/Customers"
import { fetchCustomers } from "./services/customerService";
import { APP_CATALOG, DEFAULT_ENABLED_APP_IDS } from "./navigation/appCatalog";
import Questionaire from "./pages/Questionaire"
import Discount from "./pages/Discount"

const ERP_URL =
  process.env.REACT_APP_ERP_URL || "http://localhost:3009";
const AUTH_SESSION_URL =
  process.env.REACT_APP_AUTH_SESSION_URL || "http://localhost:8086/auth/session";
const AUTH_LOGOUT_URL =
  process.env.REACT_APP_AUTH_LOGOUT_URL || "http://localhost:8086/auth/logout";
const UNAUTH_REDIRECT_URL =
  process.env.REACT_APP_UNAUTH_REDIRECT_URL || ERP_URL;
const LOGIN_URL = `${UNAUTH_REDIRECT_URL.replace(/\/$/, "")}/login-client`;
const APP_SUITE_DASHBOARD_URL =
  process.env.REACT_APP_APP_SUITE_DASHBOARD_URL || ERP_URL;
const HOME_LINK = { href: APP_SUITE_DASHBOARD_URL };
const CURRENT_APP = APP_CATALOG.find((app) => app.id === "service");
const AppLogo = CURRENT_APP.image;
const PAGE_LINKS = [
  { id: "catalog", label: "Catalog", to: "/catalog", icon: <AiOutlineShop size={24} /> },
  { id: "transactions", label: "Transactions", to: "/transactions", icon: <IoReceiptOutline size={24} /> },
  { id: "customers", label: "Customers", to: "/customers", icon: <FiUsers size={24} /> },
  { id: "questionaire", label: "Questionnaire", to: "/questionaire", icon: <AiOutlineForm size={24} /> },
  { id: "discount", label: "Discount", to: "/discount", icon: <AiOutlineTag size={24} /> },
];

function App({ enabledAppIds = DEFAULT_ENABLED_APP_IDS }) {
  const [checkingSession, setCheckingSession] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const isSmallScreen = useIsSmallScreen();
  const session = useUserStore((state) => state.session);
  const userId = session?.profile?.id;
  const userName = [session?.profile?.first_name, session?.profile?.last_name].filter(Boolean).join(" ");
  const setSession = useUserStore((state) => state.setSession);
  const clearUser = useUserStore((state) => state.clearUser);
  const fetchCatalogProducts = useProductCatalogStore((state) => state.fetchProducts);
  const activeId = location.pathname.startsWith("/transactions") || location.pathname.startsWith("/orders")
    ? "transactions"
    : location.pathname.startsWith("/customers")
    ? "customers"
    : location.pathname.startsWith("/questionaire")
    ? "questionaire"
    : location.pathname.startsWith("/discount")
    ? "discount"
    : "catalog";
  const activePage = PAGE_LINKS.find((item) => item.id === activeId).label;
  const displayName = userName || `User ${userId}`;
  const isVariableProductRoute = location.pathname.startsWith("/variableProductPage/");
  const setCustomers = useCustomerStore((state) => state.setCustomers);
  const permittedApps = session?.permitted_apps;
  const appLinks = useMemo(() => filterAppCatalog(
    filterAppCatalog(APP_CATALOG, enabledAppIds),
    permittedApps ?? []
  ), [enabledAppIds, permittedApps]);

  async function handleLogout() {
    try {
      await fetch(AUTH_LOGOUT_URL, {
        method: "POST",
        credentials: "include",
      });
    } finally {
      clearUser();
      window.location.replace(LOGIN_URL);
    }
  }

  useEffect(() => {
    let isActive = true;

    async function validateSession() {
      try {
        const response = await fetch(AUTH_SESSION_URL, {
          method: "GET",
          credentials: "include",
        });

        if (!response.ok) {
          clearUser();
          window.location.replace(LOGIN_URL);
          return;
        }

        const payload = await response.json();
        if (isActive) {
          setSession(payload);
          setCheckingSession(false);
        }
      } catch (error) {
        console.error("Session check failed:", error);
        clearUser();
        window.location.replace(LOGIN_URL);
      }
    }

    validateSession();

    return () => {
      isActive = false;
    };
  }, [clearUser, setSession]);

  useEffect(() => {
    if (checkingSession || !session) return;
    fetchCatalogProducts();
  }, [checkingSession, session, fetchCatalogProducts]);

  useEffect(() => {
    if (checkingSession || !session) return;
    async function loadCustomers() {
      try {
        const payload = await fetchCustomers();
        setCustomers(payload?.customers || []);
      } catch (error) {
        console.error("Failed to fetch customers:", error);
      }
    }

    loadCustomers();
  }, [checkingSession, session, setCustomers]);

  return (
    <div className={`crm-layout app${isSmallScreen ? " crm-layout-mobile" : ""}`}>
      {!isSmallScreen && (
        <div className="nav-section">
              <NavBar
                activeId={activeId}
                navItems={PAGE_LINKS}
                LinkComponent={Link}
                homeLink={HOME_LINK}
              />
        </div>
      )}
      {!isSmallScreen && (
        <div className="page-logo-container">
          <AppLogo
            role="img"
            aria-label="Service app"
            className="nav-logo" />
        </div>
      )}
        <PageHeader
          title={isVariableProductRoute ? "Service details" : activePage}
          apps={appLinks}
          navItems={PAGE_LINKS}
          activeId={activeId}
          activeAppId="service"
          LinkComponent={Link}
          homeLink={HOME_LINK}
          isSmallScreen={isSmallScreen}
          user={{ name: displayName }}
          logoutAction={{ onClick: handleLogout }}
          userMenu={{ name: displayName, onLogout: handleLogout }}
          back={isVariableProductRoute}
          onBack={() => {
            if (window.history.length > 1) {
              navigate(-1);
              return;
            }

            navigate("/catalog");
          }}
        />
      <Routes>
        <Route path="/" element={<Navigate to="/catalog" replace />} />
        <Route path="/catalog" element={<Catalog onOpenProduct={(productId) => navigate(`/variableProductPage/${productId}`)} />}/>
        <Route path="/transactions" element={<Orders />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/variableProductPage/:id" element={<VariableProductPage />} />
        <Route path="/questionaire" element={<Questionaire />} />
        <Route path="/discount" element={<Discount />} />
        <Route path="*" element={<Navigate to="/catalog" replace />} />
      </Routes>
    </div>
  );
}

export default App;
